import {Injectable} from '@angular/core';
import {fromEvent} from "rxjs";
import {MatDialog, MatDialogConfig, MatDialogRef} from "@angular/material/dialog";
import {PageViewFormComponent} from "../forms/page-view-form/page-view-form.component";
import {ComponentTypeFormDialogComponent
} from "../forms/component-type-form-dialog/component-type-form-dialog.component";
import {ChartDialog} from "../charts/chart-dialog/chart-dialog.component";
import {concatMap, filter, takeUntil, tap, map} from "rxjs/operators";
import {
    UserGroupPageFormDialogComponent
} from "../forms/user-group-page-form-dialog/user-group-page-form-dialog.component";
import {LockTemplateFormDialogComponent} from "../forms/lock-template-form-dialog/lock-template-form-dialog.component";
import {LockPolicyFormDialogComponent} from "../forms/lock-policy-form-dialog/lock-policy-form-dialog.component";
import {
    ConstantPropertyCalcTreeDialogComponent
} from "../forms/constant-property-calc-tree-dialog/constant-property-calc-tree-dialog.component";
import { EventComponentDeleteCheckDialogComponent
} from '../forms/event-component-delete-check-dialog/event-component-delete-check-dialog.component';
import {ConfirmSnackbarComponent} from "../notifications/snackbar/confirm-snackbar/confirm-snackbar.component";
import {CopyTileFormDialogComponent} from "../forms/copy-tile-form-dialog/copy-tile-form-dialog.component";
import {
    CommentPromptComponent,
    CommentPromptData
} from "../components/snackbar/comment-prompt/comment-prompt.component";
import {Series} from '../_models/series';
import {Process} from '../_models/process';
import {Group} from '../_models/group';
import {SessionState} from '../_models/session-state';
import {ModelID, KeyMap} from '../_typing/generic-types';
import {ComponentType} from "../_models/component-type";
import {EventType} from "../_models/event-type";
import {ConfigStub} from '../_typing/config-stub';
import {Component} from '../_models/component';
import {Event} from "../_models/event";
import {LockTemplateVersion} from "../_models/lock-template-version";
import {ConstantProperty} from '../_models/constant-property';
import {User} from "../_models/users";
import {MatSnackBarRef, MatSnackBarDismiss} from "@angular/material/snack-bar";
import {HeaderDataService} from './header_data.service';
import {DateTimeInstanceService} from "./date-time-instance.service";
import {NotificationService} from "./notification.service";
import {IDateTimePeriod} from "../_typing/date-time-period";

interface ICurrentValue {
    id: string;
}

export interface IFileOptions {
    title: string;
    value: string;
}

interface IParameterAttributes {
    custom_filters: any;
    component_types: string[];
    comment_append: string;
    current_value: ICurrentValue | any[];
    font_size: string;
    file_options: IFileOptions[];
    format: string;
    menu_visible: boolean;
    model: any;
    origin: any;
    original_models: any;
    selected_model: any;
    series: Series;
    series_permission_ids: ModelID[];
    process: Process;
    value: string | number;
    width: string | number;
}

type OptionalParameterAttributes = Partial<IParameterAttributes>;

interface IPosition {
    top: number;
    left: number;
    max_right: number;
    adjust_left: number;
}

type OptionalPosition = Partial<IPosition>;

interface ICustomDialogConfig {
    component: string;
    id: string;
    parameters: OptionalParameterAttributes;
    position: OptionalPosition;
}

export type CustomDialogConfig = Partial<ICustomDialogConfig>;


/**
 Repeated code in opening form dialogs
 **/

@Injectable({providedIn: 'root'})
export class FormDialogService {

    constructor(public dialog: MatDialog,
                private headerData: HeaderDataService,
                private notification: NotificationService,
                private dateInst: DateTimeInstanceService) {
    }

    async confirm(message: string, cancelText?: string, okText?: string): Promise<boolean> {
        const confirmRef: MatSnackBarRef<ConfirmSnackbarComponent> = this.notification.openConfirm(message, cancelText, okText);
        return await confirmRef.afterDismissed()
            .pipe(map((result: MatSnackBarDismiss) => {
                return result.dismissedByAction;
            })).toPromise();
    }

    editComponentType(component_type?, component_types_list?): MatDialogRef<ComponentTypeFormDialogComponent> {
        const dialogConfig: MatDialogConfig<any> = new MatDialogConfig();
        dialogConfig.data = {
            component_type: component_type ? component_type : {},
            component_types_list: component_types_list
        };
        dialogConfig.panelClass = ['default-form-dialog', 'event-type-form-dialog'];
        return this.dialog.open(ComponentTypeFormDialogComponent, dialogConfig);
    }

    openNewPageDialog(): MatDialogRef<PageViewFormComponent> {
        const sessionState = this.headerData.emptyPageJson();
        return this.dialog.open(PageViewFormComponent, {
            data: sessionState,
            panelClass: 'page-form-dialog'
        });
    }

    openCommentPrompt(config: Partial<CommentPromptData>) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.id = "comment-prompt-dialog";
        dialogConfig.backdropClass = 'transparent-backdrop';
        dialogConfig.disableClose = false;
        dialogConfig.data = config;
        dialogConfig.panelClass = 'prompt-dialog';
        return this.dialog.open(CommentPromptComponent, dialogConfig);
    }

    openUserGroupPageFormDialog(model: User | Group | SessionState): MatDialogRef<UserGroupPageFormDialogComponent> {
        const dialogConfig: MatDialogConfig<any> = new MatDialogConfig();
        dialogConfig.data = {model: model};
        dialogConfig.panelClass = ['default-form-dialog', 'user-form-dialog'];
        return this.dialog.open(UserGroupPageFormDialogComponent, dialogConfig);
    }

    openLockTemplateFormDialog(lock_template_ids?: ModelID[], lock_template_versions?: LockTemplateVersion[], component?: Component,
                               event?: Event, lock_version_dict?: KeyMap<ModelID>, property?: ConstantProperty | string):
        MatDialogRef<LockTemplateFormDialogComponent> {
        const dialogConfig: MatDialogConfig<any> = new MatDialogConfig();
        dialogConfig.data = {
            lock_template_ids: lock_template_ids, lock_template_versions: lock_template_versions,
            component: component, event: event, lock_version_dict: lock_version_dict, constant_property: property
        };
        dialogConfig.panelClass = ['default-form-dialog', 'user-form-dialog'];
        return this.dialog.open(LockTemplateFormDialogComponent, dialogConfig);
    }

    openLockPolicyFormDialog(lock_policy_ids?: ModelID[]): MatDialogRef<LockPolicyFormDialogComponent> {
        const dialogConfig: MatDialogConfig<any> = new MatDialogConfig();
        dialogConfig.data = {lock_policy_ids: lock_policy_ids};
        dialogConfig.panelClass = ['default-form-dialog', 'user-form-dialog'];
        return this.dialog.open(LockPolicyFormDialogComponent, dialogConfig);
    }

    openConstantPropertyCalcTreeDialog(component_or_event: Partial<ComponentType> | ConfigStub<EventType>,
                                       constant_property_id: ModelID): MatDialogRef<ConstantPropertyCalcTreeDialogComponent> {
        const dialogConfig: MatDialogConfig<any> = new MatDialogConfig();
        dialogConfig.data = {component_or_event: component_or_event, constant_property_id: constant_property_id};
        dialogConfig.panelClass = ['default-form-dialog', 'user-form-dialog'];
        return this.dialog.open(ConstantPropertyCalcTreeDialogComponent, dialogConfig);
    }

    eventComponentDeleteCheck(data_to_replace): MatDialogRef<EventComponentDeleteCheckDialogComponent> {
        return this.dialog.open(EventComponentDeleteCheckDialogComponent, {
            data: data_to_replace,
            panelClass: 'page-form-dialog',
            disableClose: true
        });
    }

    openCopyTileForm(tileId: ModelID, sessionStateId: ModelID): MatDialogRef<CopyTileFormDialogComponent> {
        return this.dialog.open(CopyTileFormDialogComponent, {
            data: {tileId: tileId, sessionStateId: sessionStateId},
            panelClass: 'copy-tile-form-dialog',
            disableClose: true
        });

    }

}
